import React, { Component } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Button, DialogContentText } from '@material-ui/core';
import LockPage from '../../main/pages/auth/LockPage';
import { logoutUser, lockUser } from '../../auth/store/actions';
import store from '../../store';

const COUNTDOWN_TIME_IN_SECONDS = 30;
const STORE_KEY = 'lastAction';

// Events that indicate user activity — covers mouse, keyboard, touch, scroll, and focus
// Note: 'resize' is handled separately (see _resizeHandler) to force a re-render
const ACTIVITY_EVENTS = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown', 'focus', 'visibilitychange'];

function isUserGuest() {
    // Safely check if the user is a guest
    try {
        return store.getState().persisted.auth.user.role === 'guest';
    } catch {
        return false;
    }
}

function getLockoutTimeInMinutes() {
    // Guests always get the short timeout (5 min), others respect keepLoggedIn
    if (isUserGuest()) return 5;
    // Read keepLoggedIn preference from localStorage (matches common_cognito_package contract)
    return localStorage.getItem('mm.keepLoggedIn') === 'true' ? 480 : 60;
}

class TimeoutDialog extends Component {
    state = {
        open: false,
        timeLeft: COUNTDOWN_TIME_IN_SECONDS,
        timerIsGoing: false,
        isLocked: false,
    };

    componentDidMount() {
        // If SSO (no username in localStorage), always set keepLoggedIn
        if (!localStorage.getItem('mm.login.currentUsername')) {
            localStorage.setItem('mm.keepLoggedIn', 'true');
        }
        this.setLastAction(Date.now());

        // Bound activity listener — ignores visibilitychange when tab is hidden
        this._activityListener = () => {
            // visibilitychange fires on hide too; only treat "visible" as activity
            if (document.visibilityState === 'hidden') return;
            if (this.reset()) {
                this.autoLockout();
            }
        };

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, this._activityListener, { passive: true });
        });

        // Separate resize handler to force re-render (render uses window.innerWidth for font size)
        this._resizeHandler = () => this.setState({});
        window.addEventListener('resize', this._resizeHandler, { passive: true });

        this.initInterval();
        if (sessionStorage.getItem('areWeLocked') === 'locked') {
            this.autoLockout();
        }
    }

    componentDidUpdate() {
        if (this.getLockoutStatus() === 'shouldLock') {
            this.autoLockout();
        }
    }

    componentWillUnmount() {
        const { isLocked } = this.state;
        clearInterval(this.intervalID2);
        clearInterval(this.intervalID);

        // Remove all activity listeners registered in componentDidMount
        if (this._activityListener) {
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, this._activityListener);
            });
        }

        // Remove resize handler
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
        }

        if (isLocked) {
            sessionStorage.setItem('areWeLocked', 'locked');
        }
        // Ensure countdown interval is cleared
        clearInterval(this.intervalID2);
    }

    /**
     * Returns one of: 'shouldLock', 'shouldWarn', or 'none'.
     */
    getLockoutStatus = () => {
        const now = Date.now();
        const lastAction = this.getLastAction();
        const lockoutTimeMs = 60 * 1000 * getLockoutTimeInMinutes();
        const countdownMs = COUNTDOWN_TIME_IN_SECONDS * 1000;
        const timeSinceLastAction = now - lastAction;
        const { isLocked } = this.state;
        if (!isLocked) {
            if (timeSinceLastAction >= lockoutTimeMs + countdownMs) {
                return 'shouldLock';
            }
            if (timeSinceLastAction >= lockoutTimeMs - countdownMs && timeSinceLastAction < lockoutTimeMs) {
                return 'shouldWarn';
            }
        }
        return 'none';
    };

    logOutUser = () => {
        store.dispatch(logoutUser());
        this.unlockUser();
    };

    getLastAction = () => Number(sessionStorage.getItem(STORE_KEY));

    setLastAction = (value) => {
        sessionStorage.setItem(STORE_KEY, String(value !== undefined ? value : Date.now()));
    };

    unlockUser = () =>
        new Promise((resolve) => {
            this.setLastAction();
            sessionStorage.setItem('areWeLocked', 'unlocked');
            this.setState({ isLocked: false });
            this.initInterval();
            resolve();
        });

    handleClose = () => {
        this.resetTimeLeft();
    };

    handleClickOpen = () => {
        // Prevent multiple countdowns
        clearInterval(this.intervalID2);
        if (!this.state.timerIsGoing) {
            this.startCountdown();
            this.setState({ open: true, timerIsGoing: true });
        }
    };

    reset() {
        if (this.getLockoutStatus() === 'shouldLock') {
            return true;
        }
        this.setLastAction(Date.now());
    }

    startCountdown() {
        // Always clear any existing countdown interval
        clearInterval(this.intervalID2);
        this.setState({ timeLeft: COUNTDOWN_TIME_IN_SECONDS });
        this.intervalID2 = setInterval(() => {
            const { timeLeft } = this.state;
            if (timeLeft <= 0) {
                this.resetTimeLeft();
                this.autoLockout();
            } else {
                this.setState({ timeLeft: timeLeft - 1 });
            }
        }, 1000);
    }

    autoLockout() {
        const { isLocked } = this.state;
        if (!isLocked) {
            clearInterval(this.intervalID);
            clearInterval(this.intervalID2);
            this.lockUser();
        }
    }

    lockUser() {
        const username = localStorage.getItem('mm.login.currentUsername');
        // Avoids lock screen when username is not set (includes SSO scenarios)
        if (username) {
            lockUser();
            this.setState({ isLocked: true, open: false });
            sessionStorage.setItem('areWeLocked', 'locked');
        } else {
            this.logOutUser();
        }
        clearInterval(this.intervalID);
        clearInterval(this.intervalID2);
    }

    initInterval() {
        this.intervalID = setInterval(() => {
            const status = this.getLockoutStatus();
            if (status === 'shouldWarn' && !this.state.open) {
                this.handleClickOpen();
            }
            if (status === 'shouldLock') {
                this.autoLockout();
            }
        }, 1000);
    }

    resetTimeLeft() {
        this.setState({
            open: false,
            timerIsGoing: false,
            timeLeft: COUNTDOWN_TIME_IN_SECONDS,
        });
        clearInterval(this.intervalID2);
    }

    render() {
        const { children } = this.props;
        const { isLocked, open, timeLeft } = this.state;

        if (!isLocked) {
            return (
                <>
                    {children}
                    <div className="inline-flex">
                        <Dialog open={open} onClose={this.handleClose} fullWidth maxWidth="sm">
                            <DialogTitle id="draggable-dialog-title" aria-setsize={16}>
                                Are you still there?
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText className="text-black text-16">Your session will be logged out in: {timeLeft} seconds</DialogContentText>
                            </DialogContent>
                            <div className="flex items-center justify-center">
                                <Button style={{ fontSize: window.innerWidth > 506 ? '24px' : window.innerWidth > 398 ? '20px' : '14px' }} type="submit" variant="contained" color="secondary" className="m-4 ml-4 flex w-2/3 h-60 " aria-label="Stay Logged In" onClick={this.handleClose}>
                                    Stay Logged In
                                </Button>
                            </div>
                        </Dialog>
                    </div>
                </>
            );
        }
        return <LockPage onUnlock={() => this.unlockUser()} onLogout={() => this.logOutUser()} />;
    }
}

export default TimeoutDialog;
